import { defineStore } from 'pinia';

export const USER_SETTINGS_STORE_ID = 'store-user-settings';

/**
 * Staff Portal user settings.
 *
 * `country`, `timezone`, `dateFormat`, `timeFormat` mirror the UI app's
 * settings shape — the i18n store's `d/time/dt/n/c/rt` format helpers
 * read them to build per-staff Intl locales. EMALINO treats `language`
 * and `country` as independent concepts: language drives translation,
 * country drives geographical formatting (date order, decimal
 * separator). The combined BCP-47 locale (e.g. `pl-PL`) is built
 * per-call inside the i18n store; it's the only place the two concepts
 * touch. Currency is NOT a per-user preference — the workspace config
 * is the single source of currency (EUR default); `c()`/`n()` take an
 * explicit currency code or fall back to EUR.
 *
 * For staff today these are localStorage-persisted only. When a
 * server-side staff-preferences endpoint lands, swap the persist
 * config to hydrate from the API.
 */
interface UserSettingsState {
  darkMode: boolean;
  sidebarCollapsed: boolean;
  isRouteLoading: boolean;
  title: string;
  /** ISO 3166-1 alpha-2, e.g. 'US', 'PL', 'NL'. Null = no regional preference. */
  country: string | null;
  /** IANA timezone, e.g. 'Europe/Amsterdam'. Null = browser default. */
  timezone: string | null;
  /** PHP-style date format token (e.g. 'Y-m-d', 'd/m/Y'). Drives `d()` output. */
  dateFormat: string | null;
  /** '12h' or '24h'. Drives `time()` output. */
  timeFormat: '12h' | '24h' | null;
}

export const useUserSettingsStore = defineStore(USER_SETTINGS_STORE_ID, {
  state: (): UserSettingsState => ({
    darkMode: false,
    sidebarCollapsed: false,
    isRouteLoading: false,
    title: 'Staff Portal',
    country: null,
    timezone: null,
    dateFormat: null,
    timeFormat: null,
  }),

  getters: {
    isSidebarOpen: (state) => !state.sidebarCollapsed,
  },

  actions: {
    initDarkMode() {
      // Check system preference if not explicitly set
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.darkMode = this.darkMode || prefersDark;
    },

    setDarkMode(value: boolean) {
      this.darkMode = value;
    },

    toggleDarkMode() {
      this.darkMode = !this.darkMode;
    },

    openSidebar() {
      this.sidebarCollapsed = false;
    },

    closeSidebar() {
      this.sidebarCollapsed = true;
    },

    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    },

    setRouteLoading(value: boolean) {
      this.isRouteLoading = value;
    },

    setTitle(title: string) {
      this.title = title;
    },

    setCountry(country: string | null) {
      this.country = country;
    },

    setTimezone(timezone: string | null) {
      this.timezone = timezone;
    },

    setDateFormat(format: string | null) {
      this.dateFormat = format;
    },

    setTimeFormat(format: '12h' | '24h' | null) {
      this.timeFormat = format;
    },
  },

  persist: {
    pick: ['darkMode', 'sidebarCollapsed', 'country', 'timezone', 'dateFormat', 'timeFormat'],
  },
});
